import { ModalItem } from '@/models/AppConfig';
import { AppState } from "@/models/TechnicianAppState";
import { STATE_MODULES } from '@/store/state-modules';
import { Store, mapGetters, mapState } from "vuex";
import { UtilsGetters } from "@/store/modules/utils/getters";
import { PropType, defineComponent } from "vue";
import { LoginMutations } from '@/store/modules/login/mutations';
import { LoginGetters } from '@/store/modules/login/getters';
import { LoginActions } from '@/store/modules/login/actions';
import { ConfigGetters } from '@/store/modules/config/getters';
import { loadGooglePlacesApi, funnelsUtilsServices } from '@puls/puls-general-utils';
import config from '@/config';
import ModalManager, { ModalConfirmationOptions } from './modal';
import { JobsGetters } from '@/store/modules/jobs/getters';
import { AvailableJob, CurrentJob, Timeslot } from '@/models/JobsState';
import JobCard from '@/components/ui/jobs/JobCard.vue';
import { TechnicianGetters } from '@/store/modules/technician/getters';
import { fullNameFormatter, dateTimeFormatter } from './formatters';
import { LeadsPlan } from '@/models/LeadsState';
import ImagesCarousel from '@/components/ui/common/ImagesCarousel.vue';
import { appointmentConstants, leadsConstants } from '@puls/puls-common-constants';
import { DeviceAttributeslItem, QuoteCustomerDeviceAttributes, QuoteDevice, QuoteImageItem, QuoteMode } from '@/models/QuoteState';
import { QuoteGetters } from '@/store/modules/quote/getters';
import generalUtils from '@/utils/utils';
import { CustomerDevice } from '@/models/Common';
import Pagination from '../components/pagination/Pagination.vue';

export const dependenciesValidation = defineComponent({
	computed: {
		isValidDependencies: function (this: any): boolean {
			const dependencies: string[] = this.dependencies;
			if (!dependencies || !dependencies.length) {
				return false;
			}
		
			const store: Store<AppState> = this.$store;
			const utils: { [key: string] : any } = store.getters[`${STATE_MODULES.UTILS}/${UtilsGetters.GET_DEPENDENCIES}`];
			return dependencies.every((dep: string) => !!utils[dep])
		}
	}
}) 

export const closeModal = defineComponent({
	props: {
		close: {
			type: Function,
			required: true
		},
		modalData: {
			type: Object as () => ModalItem,
			required: true
		}
	}
})

export const communicationMixin = defineComponent({
	computed: {
		communication: {
			get (): string {
				return this.$store.getters[`${STATE_MODULES.LOGIN}/${LoginGetters.COMMUNICATION}`];
			},
			set (value: string) : void {
				this.$store.commit(`${STATE_MODULES.LOGIN}/${LoginMutations.UPDATE_COMMUNICATION_VALUE}`, { value });
			}
		}
	}
})

export const verificationCodeMixin = defineComponent({
	mixins: [communicationMixin],
	methods: {
		sendCode (): void {
			this.$store.dispatch(`${STATE_MODULES.LOGIN}/${LoginActions.LOGIN}`)
		}
	}
})

export const notificationErrorsMixin = defineComponent({
	data () : any {
		return {
			errors: {}
		};
	},
	computed: {
		...mapGetters(STATE_MODULES.CONFIG, {
			notifications: ConfigGetters.NOTIFICATION_LIST
		}),
		hasErrors (): boolean {
			return Object.values(this.errors).some(a => a);
		}
	},
	watch: {
		notifications: {
			handler (newValue) { 
				if (typeof this.onNotificationsChange === 'function') {
					this.onNotificationsChange(newValue)
				} else {
					// eslint-disable-next-line
					console.error('Required method "onNotificationsChange" is not implemented in the component.')
				}
			},
			deep: true
		}
	}
})

export const GoogleMapsMixin = defineComponent({
	data (): any {
		return {};
	},
	mounted (): void {
		loadGooglePlacesApi(config.googleMapsKey, (_: any, ok: any) => {
			if (ok && typeof this.onGoogleLoaded === 'function') {
				this.onGoogleLoaded();
			}
		}, { geometry: true })
	}
})

export const logOutMixin = defineComponent({
	methods: {
		logout () {
			const options: ModalConfirmationOptions = {
				title: "Logout",
				message: "Are you sure you want to logout?"
			};
			
			ModalManager.openConfirmationModal(options, (result: boolean) => {
				if (result) {
					this.$store.dispatch(`${STATE_MODULES.LOGIN}/${LoginActions.LOGOUT}`)
				}
			});
		}
	}
})

export const modelValueGetSetMixin = defineComponent({
	props: {
		modelValue: {
			type: [String, Boolean, Number],
			required: true
		}
	},
	computed: {
		value: {
			get (): any {
				return this.modelValue
			},
			set (newValue: string | boolean | number) {
				this.$emit('update:modelValue', newValue)
			}
		}
	}
})

export const JobUUIDMixin = defineComponent({
	computed: {
		jobUUID () {
			return this.$route.params.job_uuid;
		}
	}
});

export const JobDetailsMixin = defineComponent({
	computed: {
		currentJob () : CurrentJob {
			return this.$store.getters[`${STATE_MODULES.JOBS}/${JobsGetters.GET_CURRENT_JOB}`]
		},
		isTechAssigned () : boolean {
			return this.currentJob?.details.isTechAssigned;
		},
		dateAndTime () : { date: string, time: string } {
			return dateTimeFormatter(this.currentJob.details)
		},
		customerName (): string {
			return fullNameFormatter(this.currentJob.contact)
		},
		customerPhone () : string {
			return this.currentJob?.contact.phone;
		},
		fullAddress () : string {
			return this.currentJob?.contact.full_address;
		},
		addressInstructions () : string { 
			return this.currentJob?.contact.instructions;
		},
		isJobOnAssignedStatus () : boolean {
			return this.currentJob?.details.appointment_status_id === appointmentConstants.STATUSES.ASSIGNED
		},
		isJobOnInProgressStatus () : boolean {
			return this.currentJob?.details.appointment_status_id === appointmentConstants.STATUSES.IN_PROGRESS
		},
		isJobOnDoneStatus () : boolean {
			return this.currentJob?.details.appointment_status_id === appointmentConstants.STATUSES.DONE
		},
		jobCustomerDevicesMap () : { [key: string]: CustomerDevice } {
			return this.$store.getters[`${STATE_MODULES.JOBS}/${JobsGetters.GET_CUSTOMER_DEVICES_MAP}`];
		},
		isFollowUp () : boolean {
			return !!this.currentJob?.details.is_follow_up;
		},
		isDiagnostic (): boolean {
			return !!this.currentJob?.details.is_diagnostic;
		}
	}
});

export const TechnicianMixin = defineComponent({
	computed: {
		...mapGetters(STATE_MODULES.TECHNICIAN, {
			email: TechnicianGetters.GET_EMAIL,
			phone: TechnicianGetters.GET_PHONE,
			fullName: TechnicianGetters.FULL_NAME
		}),
		isServiceProvider () : boolean {
			return this.$store.getters[`${STATE_MODULES.TECHNICIAN}/${TechnicianGetters.IS_SERVICE_PROVIDER}`]
		},
		isLeadgenPartner () : boolean {
			return this.$store.getters[`${STATE_MODULES.TECHNICIAN}/${TechnicianGetters.IS_LEADGEN_PARTNER}`]
		},
		remainingLeadsAmount () : number {
			return this.$store.getters[`${STATE_MODULES.TECHNICIAN}/${TechnicianGetters.REMAINING_LEADS_COUNT}`]
		},
		haveAvailbleLeads () : boolean {
			return !!this.remainingLeadsAmount
		},
		isCompanyOwner () : boolean {
			return this.$store.getters[`${STATE_MODULES.TECHNICIAN}/${TechnicianGetters.IS_COMPANY_OWNER}`]
		}
	}
});

export const jobListMixin = defineComponent({
	computed: {
		jobs (): AvailableJob[] {
			return this.$store.getters[`${STATE_MODULES.JOBS}/${JobsGetters.GET_JOBS_LIST}`] 
		}
	},
	components: {
		JobCard
	}
});

export const LeadsPlanMixin = defineComponent({
	props: { 
		plan: {
			type: Object as () => LeadsPlan,
			required: true
		}
	},
	computed: { 
		...mapState(STATE_MODULES.LEADS, ['selectedPlanId']),
		isCustomPlan (): boolean {
			return this.plan.id === leadsConstants.CUSTOM_PACK_PLAN_ID;
		},
		isSelected (): boolean { 
			return this.selectedPlanId === this.plan.id;
		}
	}
})

export const LeadsPlanContentMixin = defineComponent({
	mixins: [LeadsPlanMixin, modelValueGetSetMixin],
	props: {
		errorMessage: {
			type: String
		}
	}
})

export const HTTPLoaderMixin = defineComponent({
	computed: {
		isHttpInProgress (): boolean {
			return this.$store.getters[`${STATE_MODULES.CONFIG}/${ConfigGetters.IS_LOADING_IN_PROGRESS}`];
		}
	}
});

export const TimeSlotSelecionMixin = defineComponent({
	props: {
		timeSlotClicked: {
			type: Function,
			required: true
		}
	}
})

export const SelectedSlotMixin = defineComponent({
	props: {
		selectedSlot: {
			type: Object as PropType< Timeslot> | null | undefined,
			required: false
		}
	}
})

export const ImagesModalMixin = defineComponent({
	mixins: [closeModal],
	components: { ImagesCarousel },
	data () {
		return { 
			currentSlide: this.modalData.imagesData?.initialIndex
		}
	},
	computed: {
		images () : string[] {
			return this.modalData.imagesData?.images || [];
		}
	}
})

export const MarketsMixin = defineComponent({
	computed: {
		...mapGetters(STATE_MODULES.UTILS, {
			markets: UtilsGetters.GET_MARKETS
		})
	},
	methods: {
		marketName (marketId: number): string {
			return this.markets[marketId];
		} 
	}
})

export const QuoteAttributeInput = defineComponent({
	props: {
		attribute: {
			type: Object as PropType<DeviceAttributeslItem>,
			required: true
		},
		updateValue: {
			type: Function,
			required: true
		}
	},
	emits: ['blur'],
	methods: {
		getInputValue (event: Event): string {
			const target = event.target as HTMLInputElement;
			return target.value;
		},
		onChange (value: any) {
			this.updateValue(this.attribute.device_attribute_id, value)
		}
	}
})

export const ImagesPreviewMixin = defineComponent({
	props: {
		deleteImage: {
			type: Function,
			required: true
		}
	}
})

export const ImageUploadButtonMixin = defineComponent({
	props: {
		onUploadComplete: {
			type: Function,
			required: true
		},
		bucketName: { 
			type: String,
			required: true
		},
		bucketRoot: {
			type: String
		},
		maxImages: {
			type: Number,
			default: 10
		}
	}
})

export const UploadImagesMixin = defineComponent({
	mixins: [ImagesPreviewMixin, ImageUploadButtonMixin],
	props: {
		images: {
			type: Array as PropType<QuoteImageItem[]>,
			required: true
		}
	},
	computed: {
		maxLeft () {
			return this.maxImages - (this.images || []).length
		}
	}
})

export const DevicePropMixin = defineComponent({
	props: {
		device: {
			type: Object as () => CustomerDevice,
			required: true
		}
	}
})

export const QuoteSerivesPartsComputedMixins = defineComponent({
	computed: {
		quoteDevice () : QuoteDevice | null {
			// eslint-disable-next-line
			const _this: any = this 
			if (_this.device) {
				return this.$store.getters[`${STATE_MODULES.QUOTE}/${QuoteGetters.GET_QUOTE_DEVICES_MAP}`][_this.device.customerDeviceId];
			}
			
			return null;
		},
		subtotalPrice () {
			const quoteDevice = this.quoteDevice;
			if (!quoteDevice) {
				return 0;
			}
			return generalUtils.getQuoteDevicePrice(quoteDevice)
		}
	}
})

export const ServicesAndPartsMixin = defineComponent({
	mixins: [DevicePropMixin, QuoteSerivesPartsComputedMixins]
})

export const CurrentQuoteMixin = defineComponent({
	computed: {
		...mapGetters(STATE_MODULES.QUOTE, {
			deviceFreeServices: QuoteGetters.DEVICE_FREE_SERVICES,
			computeData: QuoteGetters.COMPUTE_DATA,
			priceIngredientsMap: QuoteGetters.PRICE_INGREDIENTS_MAP,
			computedTotalPrice: QuoteGetters.COMPUTED_TOTAL_PRICE
		}),
		quoteMode () : QuoteMode {
			return this.$store.getters[`${STATE_MODULES.QUOTE}/${QuoteGetters.GET_IS_IMAGE_UPLOAD}`];
		},
		totalPrice () {
			return this.$store.getters[`${STATE_MODULES.QUOTE}/${QuoteGetters.GET_QUOTE_TOTAL_PRICE}`];
		},
		isServicesAndPartsValid () {
			return this.$store.getters[`${STATE_MODULES.QUOTE}/${QuoteGetters.IS_PARTS_AND_SERVICES_VALID}`]
		},
		attributes () :QuoteCustomerDeviceAttributes[] {
			return this.$store.getters[`${STATE_MODULES.QUOTE}/${QuoteGetters.QUOTE_ATTRIBUTES}`];
		},
		quoteAttributesToShow () {
			return (this.attributes || []).filter((d: QuoteCustomerDeviceAttributes) => !!d.attributes?.length);
		}
	}
})

export const FormattersMixin = defineComponent({
	methods: {
		_formatPrice (price: number): number {
			const parts = funnelsUtilsServices.priceFormatter(price || 0).split('$')
			parts[1] = parseFloat(parts[1]).toFixed(2)
			return parts.join('$');
		}
	}
})

export const PaginationMixin = defineComponent({
	components: {
		Pagination
	},
	data (): any {
		return {
			currentPage: 1,
			limit: 20
		}
	},
	methods: {
		onPageChange (page: number) {
			this.currentPage = page;
			if (typeof this.handlePageChange === 'function') {
				this.handlePageChange({ limit: this.limit, offset: (page - 1) * this.limit });
			} else {
				// eslint-disable-next-line
				console.error('Required method "handlePageChange" is not implemented in the component.')
			}
		}
	}
})

export const TransformImagesMixin = defineComponent({
	methods: {
		transformImages (images: QuoteImageItem[] | string[]) : string[] {
			return images.map((item: QuoteImageItem | string) => {
				return typeof item === 'string' ? item : item.url
			});
		}
	}
})
